import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip.js';
import { resolveIcon } from '../icon-resolver.js';
import type { ScenarioApplication } from '../types.js';

/**
 * Round application-icon chip. Mirrors the legacy
 * `wf-audit/src/slices/common/components/application/icon/Provider.tsx`
 * styling (circle with coloured background + Fusion CDN PNG inside +
 * tooltip showing the app's display name). Engine returns
 * `{ label, type }` — we look up the icon URL + theme colour via
 * `icon-resolver` (tries `type` slug first, then `label` against the
 * legacy `alt` index, finally falls back to a deterministic coloured
 * letter chip).
 */
export function ApplicationIcon({
  app,
  size = 32,
  sizeIcon = 24,
}: {
  app: ScenarioApplication;
  size?: number;
  sizeIcon?: number;
}): JSX.Element {
  const meta = resolveIcon(app.type, app.label);
  const label = app.label ?? meta.alt;
  const hasIcon = meta.src.length > 0;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          className="flex items-center justify-center overflow-hidden rounded-full border-2 border-gray-500"
          style={{
            backgroundColor: `#${meta.theme}`,
            minWidth: `${size}px`,
            maxWidth: `${size}px`,
            height: `${size}px`,
          }}
        >
          {hasIcon ? (
            <img
              src={meta.src}
              alt={label}
              width={sizeIcon}
              height={sizeIcon}
              loading="lazy"
              style={{ objectFit: 'contain' }}
              onError={(e) => {
                // CDN URL unreachable — hide the broken-image glyph
                // and let the coloured circle stand alone.
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span
              className="text-xs font-semibold text-foreground"
              style={{ lineHeight: `${size}px` }}
            >
              {(label[0] ?? '?').toUpperCase()}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
